import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Star, 
  Users, 
  BookOpen, 
  Award,
  ArrowRight,
  Play
} from 'lucide-react';
import useBehaviorTracking from '../../hooks/useBehaviorTracking';
const CourseCard = ({ course, onEnroll, onViewDetails }) => {
  const { trackCourseClick } = useBehaviorTracking();
  const handleClick = () => {
    trackCourseClick(course.id,{title: course.title, category: course.category});
  };
  const {
    title = 'Course Title',
    description = 'Course description',
    duration_hours = 0,
    difficulty_level = 'beginner',
    average_rating = 0,
    enrollment_count = 0,
    skills_taught = [],
    similarity_score = 0,
    ai_insight = '',
    recommendation_reason = '',
    course_id
  } = course;

  const getDifficultyColor = (level) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSimilarityColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="card hover-lift group cursor-pointer"
      onClick={() => onViewDetails?.(course)}
    >
      <div className="card-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
              {title}
            </h3>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {description}
            </p>
          </div>
          {similarity_score > 0 && (
            <div className={`text-sm font-medium ${getSimilarityColor(similarity_score)}`}>
              {Math.round(similarity_score * 100)}% match
            </div>
          )}
        </div>

        {/* AI Insight */}
        {ai_insight && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-primary-800">AI Insight</p>
                <p className="text-xs text-primary-700 mt-1">{ai_insight}</p>
              </div>
            </div>
          </div>
        )}

        {/* Course Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4" />
              <span>{duration_hours}h</span>
            </div>
            <div className="flex items-center space-x-1">
              <Users className="h-4 w-4" />
              <span>{enrollment_count}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Star className="h-4 w-4 text-yellow-500" />
              <span>{average_rating.toFixed(1)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className={`badge ${getDifficultyColor(difficulty_level)}`}>
              {difficulty_level}
            </span>
            {recommendation_reason && (
              <span className="text-xs text-gray-500">{recommendation_reason}</span>
            )}
          </div>
        </div>

        {/* Skills */}
        {skills_taught.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-700 mb-2">Skills you'll learn:</p>
            <div className="flex flex-wrap gap-1">
              {skills_taught.slice(0, 3).map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {skill}
                </span>
              ))}
              {skills_taught.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  +{skills_taught.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEnroll?.(course);
            }}
            className="btn btn-primary btn-sm"
          >
            <Play className="h-4 w-4 mr-2" />
            Enroll Now
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(course);
            }}
            className="btn btn-ghost btn-sm"
          >
            View Details
            <ArrowRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default CourseCard;
